import { useState, type ReactNode } from "react";
import { Star } from "@semo/icons";

import { cn } from "../../utils/cn";

/**
 * 기업 프로필 카드·다이얼로그가 같이 쓰는 조각들.
 * 표기 규칙(퍼센트 올림·만료 임박 기준·이니셜)을 한 곳에 둬서 두 화면이 어긋나지 않게 한다.
 */

/** 상위 퍼센트 표기 — 1% 미만은 1% 로 올린다 */
export function formatTrustPercentile(percentile: number): number {
  return percentile <= 1 ? 1 : Math.round(percentile);
}

/** "2027-05-31" → "2027.05" */
export function formatYearMonth(iso: string | null): string | null {
  if (!iso) return null;
  const [year, month] = iso.slice(0, 7).split("-");
  return year && month ? `${year}.${month}` : null;
}

/** 홈페이지 주소를 사람이 읽는 도메인으로 — 파싱이 안 되면 원문 그대로 */
export function toDisplayHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

export type CertExpiryState = "none" | "valid" | "soon" | "expired";

/** 만료 30일 이내를 «임박» 으로 본다 — 씨마켓 ins-validity.policy 의 EXPIRING_SOON(<=30일)과 맞춘다 */
const CERT_EXPIRY_SOON_DAYS = 30;

export function getCertExpiryState(
  expiresAt: string | null,
  now: number = Date.now(),
): CertExpiryState {
  if (!expiresAt) return "none";
  const expiresAtMs = new Date(expiresAt).getTime();
  if (Number.isNaN(expiresAtMs)) return "none";
  const daysLeft = (expiresAtMs - now) / 86_400_000;
  if (daysLeft < 0) return "expired";
  if (daysLeft <= CERT_EXPIRY_SOON_DAYS) return "soon";
  return "valid";
}

const CORPORATE_MARKERS = /\(주\)|㈜|주식회사|\(유\)|유한회사/g;

/** 히어로에 흐리게 까는 이니셜 — 법인 표기((주)·주식회사)는 건너뛴다 */
export function getCompanyInitial(name: string): string {
  const stripped = name.replace(CORPORATE_MARKERS, "").trim();
  return stripped.charAt(0) || "S";
}

/** 아바타 이니셜용 이름 — «(주)토사코코리아» 가 «(» 로 찍히지 않게 법인 표기를 뗀다 */
export function toAvatarName(name: string): string {
  return name.replace(CORPORATE_MARKERS, "").trim() || name;
}

/**
 * 최근 접속 표기 — 로그인 시각이라 «접속 중» 이라고는 쓰지 않고 날짜 단위로 뭉갠다.
 * 오늘·어제만 초록(fresh), 그 뒤는 회색.
 */
export function describeLastActive(
  iso: string | null | undefined,
  now: number = Date.now(),
): { label: string; fresh: boolean } | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;

  const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const daysAgo = Math.round(
    (startOfDay(new Date(now)) - startOfDay(at)) / 86_400_000,
  );

  if (daysAgo <= 0) return { label: "오늘 접속", fresh: true };
  if (daysAgo === 1) return { label: "어제 접속", fresh: true };
  if (daysAgo < 7) return { label: `${daysAgo}일 전 접속`, fresh: false };
  if (daysAgo < 30)
    return { label: `${Math.floor(daysAgo / 7)}주 전 접속`, fresh: false };
  return { label: "한 달 넘게 미접속", fresh: false };
}

export function LastActiveBadge({
  lastActiveAt,
  className,
}: {
  lastActiveAt: string | null | undefined;
  className?: string;
}) {
  const activity = describeLastActive(lastActiveAt);
  if (!activity) return null;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 text-2xs",
        activity.fresh ? "text-emerald-600" : "text-slate-400",
        className,
      )}
    >
      <span
        aria-hidden
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          activity.fresh ? "bg-emerald-500" : "bg-slate-300",
        )}
      />
      {activity.label}
    </span>
  );
}

/** 0.5 단위까지 채워지는 별점 (4.7 → 별 4개 + 0.7칸) */
export function RatingStars({
  rating,
  starClassName = "h-3.5 w-3.5",
  className,
}: {
  rating: number;
  starClassName?: string;
  className?: string;
}) {
  return (
    <span
      className={cn("inline-flex items-center gap-px", className)}
      aria-hidden
    >
      {[1, 2, 3, 4, 5].map((i) => {
        const fill = Math.max(0, Math.min(1, rating - (i - 1)));
        return (
          <span key={i} className={cn("relative inline-block", starClassName)}>
            <Star
              className={cn(
                "absolute left-0 top-0 fill-slate-200 text-slate-200",
                starClassName,
              )}
            />
            {fill > 0 && (
              <span
                className="absolute left-0 top-0 h-full overflow-hidden"
                style={{ width: `${fill * 100}%` }}
              >
                <Star
                  className={cn(
                    "max-w-none fill-amber-400 text-amber-400",
                    starClassName,
                  )}
                />
              </span>
            )}
          </span>
        );
      })}
    </span>
  );
}

/**
 * 랜딩 썸네일 히어로.
 * 썸네일이 없거나 깨지면 브랜드 그라데이션 + 점 격자 + 큰 이니셜로 채워서
 * «빈 파란 띠» 가 아니라 그 회사 자리처럼 보이게 한다.
 */
export function ProfileHero({
  imageUrl,
  name,
  className,
  showInitial = true,
  initialClassName = "text-[7rem]",
  children,
}: {
  imageUrl: string | null;
  name: string;
  className?: string;
  /** 익명 표기(«익명 공급사»)의 이니셜은 뜻이 없으니 끈다 */
  showInitial?: boolean;
  /** 이니셜 글자 크기 — 히어로 높이에 맞춘다 */
  initialClassName?: string;
  children?: ReactNode;
}) {
  // 깨진 주소를 기억해 두면 URL 이 바뀔 때 따로 되돌릴 필요가 없다
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const showImage = !!imageUrl && failedUrl !== imageUrl;

  return (
    <div
      className={cn("relative w-full overflow-hidden bg-[#1e3a8a]", className)}
    >
      {showImage ? (
        <>
          <img
            src={imageUrl}
            alt=""
            className="h-full w-full object-cover"
            onError={() => setFailedUrl(imageUrl)}
          />
          <div
            aria-hidden
            className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/0 to-black/20"
          />
        </>
      ) : (
        <div
          aria-hidden
          className="absolute inset-0 bg-[radial-gradient(130%_140%_at_0%_0%,#4f7ae6_0%,#2748a6_48%,#172b66_100%)]"
        >
          <div className="absolute inset-0 opacity-20 [background-image:radial-gradient(rgba(255,255,255,0.85)_1px,transparent_1px)] [background-size:14px_14px]" />
          {showInitial && (
            <span
              className={cn(
                "absolute -bottom-[0.18em] right-3 select-none font-black leading-none text-white/10",
                initialClassName,
              )}
            >
              {getCompanyInitial(name)}
            </span>
          )}
        </div>
      )}
      {children}
    </div>
  );
}
